import dayjs from 'dayjs';

const MINUTES_PER_HOUR = 60;
const MINUTES_PER_DAY = 60 * 24;

function plural(count: number, unit: string): string {
	return `${count} ${unit}${count === 1 ? '' : 's'}`;
}

/**
 * The span split into the units it is said in, or undefined for a span of nothing or less.
 *
 * Days as well as hours, because a charter can be handed back the following morning and "26 hours"
 * is not how anyone says that.
 *
 * Nothing to say about a start and end the wrong way round: the field already carries that message,
 * and a duration beside it would be a second, vaguer way of saying the same thing.
 */
function breakdown(start: Date, end: Date): { days: number; hours: number; minutes: number } | undefined {
	const total = dayjs(end).diff(start, 'minute');

	if (total <= 0) {
		return undefined;
	}

	return {
		days: Math.floor(total / MINUTES_PER_DAY),
		hours: Math.floor((total % MINUTES_PER_DAY) / MINUTES_PER_HOUR),
		minutes: total % MINUTES_PER_HOUR,
	};
}

/**
 * How long a charter holds the vessel, in words — "3 hours", "1 hour 30 minutes", "1 day 2 hours".
 */
export function formatCharterDuration(start: Date, end: Date): string | undefined {
	const span = breakdown(start, end);

	if (!span) {
		return undefined;
	}

	const parts = [
		span.days > 0 ? plural(span.days, 'day') : undefined,
		span.hours > 0 ? plural(span.hours, 'hour') : undefined,
		span.minutes > 0 ? plural(span.minutes, 'minute') : undefined,
	];

	return parts.filter(Boolean).join(' ');
}

/**
 * The same span at chip length — "3h", "1h 30m", "1d 2h".
 *
 * Abbreviated because it sits on the label row of the field it describes, which is half a card wide
 * and already carrying a label. Anywhere with room for the words should use those instead.
 */
export function formatCharterDurationShort(start: Date, end: Date): string | undefined {
	const span = breakdown(start, end);

	if (!span) {
		return undefined;
	}

	const parts = [
		span.days > 0 ? `${span.days}d` : undefined,
		span.hours > 0 ? `${span.hours}h` : undefined,
		span.minutes > 0 ? `${span.minutes}m` : undefined,
	];

	return parts.filter(Boolean).join(' ');
}
